import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Palette, Users, Rocket, ArrowRight, ArrowLeft, X, Plus, Trash, CheckCircle, Upload } from "@phosphor-icons/react";

const STEPS = [
  { id: "branding", label: "Branding", icon: Palette },
  { id: "team", label: "Invite Team", icon: Users },
  { id: "launch", label: "Launch", icon: Rocket },
];

const COLOR_PRESETS = [
  "#1D4ED8", "#7C3AED", "#059669", "#DC2626", "#EA580C",
  "#0891B2", "#4F46E5", "#BE185D", "#65A30D", "#0D9488",
];

export default function SetupWizardPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Branding state
  const [logoUrl, setLogoUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#1D4ED8");
  const [tenant, setTenant] = useState(null);

  // Team invite state
  const [invites, setInvites] = useState([{ email: "", name: "", role: "team_member" }]);
  const [inviteResults, setInviteResults] = useState(null);

  useEffect(() => {
    API.get("/tenant/current").then(({ data }) => {
      setTenant(data);
      setCompanyName(data.branding?.company_name || data.name || "");
      setLogoUrl(data.branding?.logo_url || "");
      setPrimaryColor(data.branding?.primary_color || "#1D4ED8");
    }).catch(() => {});
  }, []);

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await API.put("/tenant/branding", {
        logo_url: logoUrl,
        company_name: companyName,
        primary_color: primaryColor,
      });
      toast.success("Branding saved");
      setCurrentStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  const handleInviteTeam = async () => {
    const validInvites = invites.filter((i) => i.email.trim());
    if (validInvites.length === 0) {
      setCurrentStep(2);
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.post("/tenant/invite-users", { invites: validInvites });
      setInviteResults(data);
      if (data.created?.length > 0) {
        toast.success(`${data.created.length} team member(s) invited`);
      }
      setCurrentStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite users");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await API.post("/tenant/setup-complete");
      await checkAuth();
      toast.success("Setup complete! Welcome to your workspace.");
      navigate("/");
    } catch {
      navigate("/");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    await API.post("/tenant/setup-complete").catch(() => {});
    await checkAuth();
    navigate("/");
  };

  const addInviteRow = () => {
    setInvites([...invites, { email: "", name: "", role: "team_member" }]);
  };

  const removeInviteRow = (idx) => {
    setInvites(invites.filter((_, i) => i !== idx));
  };

  const updateInvite = (idx, field, value) => {
    setInvites(invites.map((inv, i) => (i === idx ? { ...inv, [field]: value } : inv)));
  };

  const StepIcon = STEPS[currentStep]?.icon || Rocket;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="setup-wizard">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 mb-3">
            <StepIcon size={24} className="text-primary" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight">Set Up Your Workspace</h1>
          <p className="text-sm text-muted-foreground mt-1">Just a few quick steps to get you started</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="setup-steps">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${i === currentStep ? "bg-primary text-primary-foreground" : i < currentStep ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                {i < currentStep ? <CheckCircle size={12} weight="fill" /> : <span className="font-mono">{i + 1}</span>}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-8 h-px ${i < currentStep ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="border border-border rounded-xl bg-card p-6">
          {/* Step 1: Branding */}
          {currentStep === 0 && (
            <div className="space-y-5" data-testid="setup-step-branding">
              <div>
                <h2 className="text-base font-bold font-[Chivo] mb-1">Brand Your Workspace</h2>
                <p className="text-xs text-muted-foreground">Customise the look of your platform</p>
              </div>

              <div>
                <Label>Company Name</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company Ltd"
                  className="mt-1"
                  data-testid="setup-company-name"
                />
              </div>

              <div>
                <Label>Logo URL</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://yoursite.com/logo.png"
                    className="flex-1"
                    data-testid="setup-logo-url"
                  />
                  {logoUrl && (
                    <div className="h-9 w-9 rounded border border-border flex items-center justify-center bg-muted overflow-hidden shrink-0">
                      <img src={logoUrl} alt="Logo" className="h-7 w-7 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Paste a URL to your company logo (PNG or SVG recommended)</p>
              </div>

              <div>
                <Label>Primary Colour</Label>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex gap-1.5 flex-wrap">
                    {COLOR_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setPrimaryColor(c)}
                        className={`h-7 w-7 rounded-full border-2 transition-all ${primaryColor === c ? "border-foreground scale-110" : "border-transparent"}`}
                        style={{ backgroundColor: c }}
                        data-testid={`setup-color-${c}`}
                      />
                    ))}
                  </div>
                  <Input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-7 w-9 p-0 border-0 cursor-pointer"
                    data-testid="setup-color-picker"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="border border-border rounded-lg p-4 bg-muted/30" data-testid="setup-branding-preview">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2 font-bold">Preview</p>
                <div className="flex items-center gap-2">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Preview" className="h-8 w-8 object-contain rounded" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="h-8 w-8 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: primaryColor }}>
                      {companyName?.charAt(0)?.toUpperCase() || "C"}
                    </div>
                  )}
                  <span className="text-sm font-bold font-[Chivo]" style={{ color: primaryColor }}>
                    {companyName || "Your Company"}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCurrentStep(1)} data-testid="setup-skip-branding">
                  Skip for Now
                </Button>
                <Button className="flex-1" onClick={handleSaveBranding} disabled={saving} data-testid="setup-save-branding">
                  {saving ? "Saving..." : "Save & Continue"}
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Invite Team */}
          {currentStep === 1 && (
            <div className="space-y-5" data-testid="setup-step-team">
              <div>
                <h2 className="text-base font-bold font-[Chivo] mb-1">Invite Your Team</h2>
                <p className="text-xs text-muted-foreground">Add team members who will use the platform</p>
              </div>

              <div className="space-y-3">
                {invites.map((inv, idx) => (
                  <div key={idx} className="flex items-end gap-2" data-testid={`setup-invite-row-${idx}`}>
                    <div className="flex-1">
                      {idx === 0 && <Label className="text-[10px]">Email</Label>}
                      <Input
                        type="email"
                        value={inv.email}
                        onChange={(e) => updateInvite(idx, "email", e.target.value)}
                        placeholder="colleague@company.com"
                        className="mt-0.5"
                        data-testid={`setup-invite-email-${idx}`}
                      />
                    </div>
                    <div className="w-28">
                      {idx === 0 && <Label className="text-[10px]">Name</Label>}
                      <Input
                        value={inv.name}
                        onChange={(e) => updateInvite(idx, "name", e.target.value)}
                        placeholder="Name"
                        className="mt-0.5"
                        data-testid={`setup-invite-name-${idx}`}
                      />
                    </div>
                    <div className="w-32">
                      {idx === 0 && <Label className="text-[10px]">Role</Label>}
                      <Select value={inv.role} onValueChange={(v) => updateInvite(idx, "role", v)}>
                        <SelectTrigger className="mt-0.5 h-9" data-testid={`setup-invite-role-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="project_manager">PM</SelectItem>
                          <SelectItem value="team_member">Team</SelectItem>
                          <SelectItem value="vendor">Supplier</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {invites.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeInviteRow(idx)} data-testid={`setup-invite-remove-${idx}`}>
                        <Trash size={14} className="text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <Button variant="outline" size="sm" onClick={addInviteRow} className="text-xs" data-testid="setup-add-invite">
                <Plus size={12} className="mr-1" /> Add Another
              </Button>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setCurrentStep(0)} data-testid="setup-back-branding">
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setCurrentStep(2)} data-testid="setup-skip-team">
                  Skip
                </Button>
                <Button className="flex-1" onClick={handleInviteTeam} disabled={saving} data-testid="setup-send-invites">
                  {saving ? "Sending..." : "Invite & Continue"}
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Launch */}
          {currentStep === 2 && (
            <div className="text-center space-y-5 py-4" data-testid="setup-step-launch">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30">
                <Rocket size={32} className="text-emerald-600" weight="duotone" />
              </div>
              <div>
                <h2 className="text-lg font-bold font-[Chivo] mb-1">You're All Set!</h2>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  Your workspace is ready. Start managing your properties, suppliers, and projects.
                </p>
              </div>

              {inviteResults?.created?.length > 0 && (
                <div className="border border-border rounded-lg p-3 text-left bg-muted/30" data-testid="setup-invite-results">
                  <p className="text-xs font-semibold mb-2">Team Members Invited:</p>
                  {inviteResults.created.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-border last:border-0">
                      <span>{c.email} <span className="text-muted-foreground capitalize">({c.role.replace("_", " ")})</span></span>
                      <code className="text-[9px] bg-muted px-1.5 py-0.5 rounded font-mono">{c.temp_password}</code>
                    </div>
                  ))}
                  <p className="text-[9px] text-muted-foreground mt-2">Share these temporary passwords with your team. They can change them after first login.</p>
                </div>
              )}

              <Button size="lg" className="w-full max-w-xs mx-auto" onClick={handleComplete} disabled={saving} data-testid="setup-launch-btn">
                {saving ? "Launching..." : "Launch Dashboard"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* Skip entire setup */}
        <div className="text-center mt-4">
          <button onClick={handleSkip} className="text-xs text-muted-foreground hover:text-foreground hover:underline" data-testid="setup-skip-all">
            Skip setup — I'll configure later
          </button>
        </div>
      </div>
    </div>
  );
}
